import { useEffect, useState } from 'react';
import { api, type CatalogueItem } from '../api';
import type { PatientSource } from '../api-modules';
import { useI18n } from '../i18n';
import { EmptyState, ErrorState, Field, Modal, Skeleton, Spinner, Tabs } from '../components';
import { PermissionEditor } from '../components/PermissionEditor';
import { Transfer } from './Transfer';
import { formatBytes, minorToInput, parseToMinor } from '../money';
import { describe } from '../errors';

type ClinicSettings = Awaited<ReturnType<typeof api.clinicSettings>>;

export function Settings({ clinicId, permissions, onClinicChanged }: {
  clinicId: string;
  permissions: string[];
  /** The clinic's own record changed (name, currency, what it practises): the shell re-reads the session so navigation and tabs follow. */
  onClinicChanged?: () => void;
}) {
  const { t } = useI18n();
  const [tab, setTab] = useState<
    'general' | 'public' | 'staff' | 'catalogue' | 'sources' | 'data' | 'audit'
  >('general');
  const can = (p: string) => permissions.includes(p);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.settings.title}</h1>
        </div>
      </div>
      <Tabs
        value={tab}
        onChange={(v) => setTab(v as typeof tab)}
        tabs={[
          { value: 'general', label: t.settings.general },
          { value: 'public', label: t.settings.publicPage },
          { value: 'staff', label: t.settings.staff, hidden: !can('clinic.staff.manage') },
          { value: 'catalogue', label: t.settings.catalogue },
          { value: 'sources', label: t.sources.title, hidden: !can('clinic.settings.manage') },
          {
            value: 'data',
            label: t.transfer.title,
            // Hidden only when the user can do NEITHER half; the panel itself
            // greys out the individual exports they lack.
            hidden: !can('patient.export') && !can('clinic.import') && !can('finance.reports.read'),
          },
          { value: 'audit', label: t.settings.audit, hidden: !can('clinic.audit.read') },
        ]}
      />
      {tab === 'general' || tab === 'public' ? <General clinicId={clinicId} publicTab={tab === 'public'} onSaved={onClinicChanged} /> : null}
      {tab === 'staff' ? <Staff clinicId={clinicId} permissions={permissions} /> : null}
      {tab === 'catalogue' ? <Catalogue clinicId={clinicId} /> : null}
      {tab === 'sources' ? <Sources clinicId={clinicId} /> : null}
      {tab === 'data' ? <Transfer canImport={can('clinic.import')} /> : null}
      {tab === 'audit' ? <Audit clinicId={clinicId} /> : null}
    </>
  );
}

function General({ clinicId, publicTab, onSaved }: { clinicId: string; publicTab: boolean; onSaved?: () => void }) {
  const { t, locale } = useI18n();
  const [addingChair, setAddingChair] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [data, setData] = useState<ClinicSettings | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setData(null);
    api
      .clinicSettings()
      .then((d) => {
        setData(d);
        setForm({ ...d.clinic });
      })
      .catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.updateClinicSettings(form);
      setSaved(true);
      onSaved?.();
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} />;
  if (!data) return <div className="card"><Skeleton rows={6} /></div>;

  const set = (k: string) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  const setBool = (k: string) => (e: { target: { checked: boolean } }) => setForm({ ...form, [k]: e.target.checked });

  return (
    <div className="stack">
      {saved ? <div className="notice" role="status"><span>{t.settings.saved}</span></div> : null}

      {!publicTab ? <ChangePassword /> : null}

      {!publicTab ? (
        <div className="card">
          <div className="card-head"><h2>{t.settings.general}</h2></div>
          <div className="card-body stack">
            <div className="grid-2">
              <Field label={`${t.settings.name} (EN)`}>
                <input value={String(form.nameEn ?? '')} onChange={set('nameEn')} />
              </Field>
              <Field label={`${t.settings.name} (AR)`}>
                <input value={String(form.nameAr ?? '')} onChange={set('nameAr')} dir="rtl" />
              </Field>
              <Field label={t.settings.timezone} hint="IANA, e.g. Asia/Damascus">
                <input value={String(form.timezone ?? '')} onChange={set('timezone')} className="ltr-value" />
              </Field>
              <Field label={t.settings.currency}>
                <input value={String(form.defaultCurrency ?? '')} onChange={set('defaultCurrency')} className="ltr-value" maxLength={3} />
              </Field>
              <Field label={t.settings.countryCode} hint="963, 966, 971">
                <input value={String(form.countryCode ?? '')} onChange={set('countryCode')} className="ltr-value" />
              </Field>
              <Field label={t.patients.phone}>
                <input value={String(form.phone ?? '')} onChange={set('phone')} className="ltr-value" />
              </Field>
            </div>
            <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
              <input type="checkbox" checked={Boolean(form.twoStepEverySignIn)} onChange={setBool('twoStepEverySignIn')} style={{ marginTop: 3 }} />
              <span>
                <span style={{ display: 'block', fontWeight: 600 }}>{t.settings.everySignIn}</span>
                <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12.5 }}>{t.settings.everySignInHint}</span>
              </span>
            </label>
            {/* How dentists dictate notes: on the computer (private) or the browser's service. */}
            <Field label={t.dictation.settingTitle} hint={t.dictation.settingSub}>
              <div className="stack" style={{ gap: 8 }}>
                {(['device', 'browser', 'off'] as const).map((m) => (
                  <label key={m} className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
                    <input
                      type="radio"
                      name="dictation"
                      checked={(form.dictation ?? 'device') === m}
                      onChange={() => setForm({ ...form, dictation: m })}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      <span style={{ display: 'block', fontWeight: 600 }}>{t.dictation[m]}</span>
                      {m !== 'off' ? (
                        <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12.5 }}>{m === 'device' ? t.dictation.deviceHint : t.dictation.browserHint}</span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            </Field>
            {/* What the clinic practises decides which charts its patient files show. */}
            <Field label={t.settings.specialties} hint={t.settings.specialtiesHint}>
              <div className="row" style={{ flexWrap: 'wrap', gap: 14 }}>
                {(['dental', 'dermatology', 'aesthetics'] as const).map((sp) => {
                  const list = Array.isArray(form.specialties) ? (form.specialties as string[]) : ['dental'];
                  return (
                    <label key={sp} className="row" style={{ gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={list.includes(sp)}
                        onChange={(e) => {
                          const next = e.target.checked ? [...list, sp] : list.filter((x) => x !== sp);
                          setForm({ ...form, specialties: next.length ? next : list });
                        }}
                      />
                      <span>{(t.settings.specialty as Record<string, string>)[sp]}</span>
                    </label>
                  );
                })}
              </div>
            </Field>
            <button type="button" className="btn" onClick={() => void save()} disabled={busy}>
              {busy ? <Spinner /> : t.settings.save}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="card-head">
              <h2>{t.settings.publicPage}</h2>
              {form.publicPagePublished ? (
                <a
                  className="btn btn-ghost btn-sm"
                  href={`/c/${data.clinic.slug}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t.settings.viewUrl}
                </a>
              ) : null}
            </div>
            <div className="card-body stack">
              <div className="grid-2">
                <Field label={`${t.settings.description} (EN)`}>
                  <textarea value={String(form.descriptionEn ?? '')} onChange={set('descriptionEn')} rows={4} />
                </Field>
                <Field label={`${t.settings.description} (AR)`}>
                  <textarea value={String(form.descriptionAr ?? '')} onChange={set('descriptionAr')} rows={4} dir="rtl" />
                </Field>
              </div>
              <Field label={t.settings.address}>
                <input value={String(form.address ?? '')} onChange={set('address')} />
              </Field>

              <label className="row" style={{ gap: 8 }}>
                <input type="checkbox" checked={Boolean(form.publicPagePublished)} onChange={setBool('publicPagePublished')} />
                <span>{t.settings.publish}</span>
              </label>
              <label className="row" style={{ gap: 8 }}>
                <input type="checkbox" checked={Boolean(form.showTeamPublicly)} onChange={setBool('showTeamPublicly')} />
                <span>{t.settings.showTeam}</span>
              </label>

              <Field label={t.settings.bookingMode} hint={t.settings.bookingHint}>
                <select value={String(form.publicBookingMode ?? 'request')} onChange={set('publicBookingMode')}>
                  {(['request', 'disabled', 'verified_auto'] as const).map((m) => (
                    <option key={m} value={m}>
                      {(t.settings.bookingModes as Record<string, string>)[m]}
                    </option>
                  ))}
                </select>
              </Field>

              <button type="button" className="btn" onClick={() => void save()} disabled={busy}>
                {busy ? <Spinner /> : t.settings.save}
              </button>
            </div>
          </div>
        </>
      )}

      <div className="grid-2" style={{ alignItems: 'start' }}>
        <div className="card">
          <div className="card-head"><h2>{t.settings.subscription}</h2></div>
          <div className="card-body">
            {data.subscription ? (
              <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', margin: 0 }}>
                <dt style={{ color: 'var(--ink-3)' }}>{t.settings.plan}</dt>
                <dd style={{ margin: 0 }}><span className="badge brand">{data.subscription.plan}</span></dd>
                <dt style={{ color: 'var(--ink-3)' }}>{t.settings.seats}</dt>
                <dd style={{ margin: 0 }}>
                  {data.subscription.maxActiveUsers ?? '—'} users · {data.subscription.maxDentists ?? '—'} dentists
                </dd>
                <dt style={{ color: 'var(--ink-3)' }}>{t.settings.storageUsed}</dt>
                <dd style={{ margin: 0 }} className="ltr-value">{formatBytes(data.usage.storageBytes)}</dd>
              </dl>
            ) : (
              <p style={{ margin: 0, color: 'var(--ink-3)' }}>—</p>
            )}
          </div>
        </div>

        {/*
          Chairs were listed and could not be added, so a clinic that bought a
          third one had nowhere to say so — and the calendar's chair columns
          come from this list.
        */}
        <div className="card">
          <div className="card-head">
            <h2>
              {t.settings.chairs}
              <span className="count-chip">{data.chairs.length}</span>
            </h2>
            <button type="button" className="btn-link" onClick={() => setAddingChair(true)}>
              + {t.settings.addChair}
            </button>
          </div>
          <div className="card-body">
            {data.chairs.length === 0 ? (
              <p style={{ color: 'var(--ink-3)', margin: 0 }}>{t.settings.noChairs}</p>
            ) : (
              <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                {data.chairs.map((c) => (
                  <span key={c.id} className="badge">
                    {locale === 'ar' ? c.nameAr : c.nameEn}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {addingChair ? (
        <NewChair
          onClose={() => setAddingChair(false)}
          onDone={() => {
            setAddingChair(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * A new chair, room or piece of equipment.
 *
 * Named in both languages because it appears as a column heading on the
 * calendar, which a clinic reads in whichever language it works in.
 */
function NewChair({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [nameEn, setNameEn] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [kind, setKind] = useState<'chair' | 'room' | 'equipment'>('chair');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal
      title={t.settings.addChair}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !nameEn.trim()}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.createChair({
                  nameEn: nameEn.trim(),
                  nameAr: nameAr.trim() || nameEn.trim(),
                  kind,
                });
                onDone();
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : t.settings.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}
        <div className="grid-2">
          <Field label={t.settings.name} required>
            <input value={nameEn} onChange={(e) => setNameEn(e.target.value)} autoFocus />
          </Field>
          <Field label={`${t.settings.name} (العربية)`}>
            <input value={nameAr} onChange={(e) => setNameAr(e.target.value)} dir="rtl" />
          </Field>
        </div>
        <Field label={t.settings.resourceKind} required>
          <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            {(['chair', 'room', 'equipment'] as const).map((k) => (
              <option key={k} value={k}>
                {(t.settings.resourceKinds as Record<string, string>)[k]}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Staff and permissions.
 *
 * Shows the role template, explicit overrides and the resulting effective set,
 * so "why can this person see that?" always has a visible answer. The
 * delegation rule is enforced on the server; the note states it here (spec §4).
 */
function Staff({ clinicId, permissions }: { clinicId: string; permissions: string[] }) {
  const { t } = useI18n();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.staff>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setData(null);
    api.staff().then(setData).catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;
  if (!data) {
    return (
      <div className="card">
        <Skeleton rows={5} />
      </div>
    );
  }

  const member = data.staff.find((m) => m.membershipId === editing) ?? null;

  return (
    <div className="stack">
      <div className="notice">
        <span>{t.settings.delegationNote}</span>
      </div>

      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn" onClick={() => setInviting(true)}>
          + {t.settings.invite}
        </button>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t.inventory.name}</th>
                <th>{t.settings.role}</th>
                <th>{t.settings.permissions}</th>
                <th>{t.common.status}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.staff.map((m) => (
                <tr key={m.membershipId}>
                  <td>
                    <strong>{m.name}</strong>
                    <span className="sub ltr-value">{m.email}</span>
                  </td>
                  <td>
                    {m.roles.map((r) => (
                      <span key={r} className="badge" style={{ marginInlineEnd: 4 }}>
                        {r.replace(/_/g, ' ')}
                      </span>
                    ))}
                    {m.isDentist ? (
                      <span className="badge brand">{m.publicTitle ?? t.nav.dentists}</span>
                    ) : null}
                  </td>
                  <td>
                    {/* The role total and this person's own adjustments are
                        shown separately, so "receptionist" plus two extras
                        never reads as a different role. */}
                    {m.effectivePermissions.length} {t.settings.effective}
                    {m.granted.length > 0 ? (
                      <span className="badge ok" style={{ marginInlineStart: 6 }}>
                        +{m.granted.length}
                      </span>
                    ) : null}
                    {m.revoked.length > 0 ? (
                      <span className="badge danger" style={{ marginInlineStart: 6 }}>
                        −{m.revoked.length}
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <span className={`badge ${m.status === 'active' ? 'ok' : 'warn'}`}>
                      {m.status === 'invited' ? t.settings.invited : m.status}
                    </span>
                  </td>
                  <td style={{ textAlign: 'end' }}>
                    {m.status === 'invited' ? <ResendInvite membershipId={m.membershipId} /> : null}
                    <button type="button" className="btn-link" onClick={() => setEditing(m.membershipId)}>
                      {t.settings.editPermissions}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {member ? (
        <PermissionEditor
          member={member}
          roleTemplates={data.roleTemplates}
          allPermissions={data.allPermissions}
          myPermissions={permissions}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {inviting ? (
        <InviteStaff
          roles={Object.keys(data.roleTemplates).filter((r) => r !== 'platform_owner')}
          onClose={() => {
            setInviting(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Invite a colleague.
 *
 * The code is shown exactly once, here, and never again: the server keeps
 * only a hash of it. Closing the dialog is the moment it is gone, so the
 * dialog says so before it closes.
 */
function InviteStaff({ roles, onClose }: { roles: string[]; onClose: () => void }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ email: '', fullName: '', role: roles.includes('receptionist') ? 'receptionist' : roles[0] ?? 'dentist', isDentist: false, publicTitle: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ email: string; emailed: boolean } | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.inviteStaff({
        email: form.email.trim(),
        fullName: form.fullName.trim(),
        roles: [form.role],
        isDentist: form.isDentist || form.role === 'dentist',
        publicTitle: form.publicTitle.trim() || null,
      });
      setIssued({ email: r.email, emailed: r.emailed });
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t.settings.invite}
      onClose={onClose}
      footer={
        issued ? (
          <button type="button" className="btn" onClick={onClose}>
            {t.common.close}
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
              {t.patients.cancel}
            </button>
            <button type="button" className="btn" disabled={busy || !form.email.trim() || form.fullName.trim().length < 2} onClick={() => void submit()}>
              {busy ? <Spinner /> : t.settings.invite}
            </button>
          </>
        )
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}
        {issued ? (
          <div className={issued.emailed ? 'notice ok' : 'notice'} role="status">
            <span>{(issued.emailed ? t.settings.invitationEmailed : t.settings.invitedDone).replace('{email}', issued.email)}</span>
          </div>
        ) : (
          <>
            <div className="notice">
              <span>{t.settings.inviteHint}</span>
            </div>
            <div className="grid-2">
              <Field label={t.auth.email} required>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ltr-value" autoFocus />
              </Field>
              <Field label={t.settings.inviteeName} required>
                <input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
              </Field>
              <Field label={t.settings.role} required>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  {roles.map((r) => (
                    <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </Field>
              <Field label={t.dentists.publicTitle} hint={t.dentists.publicTitleHint}>
                <input value={form.publicTitle} onChange={(e) => setForm({ ...form, publicTitle: e.target.value })} />
              </Field>
            </div>
            <label className="row" style={{ gap: 8 }}>
              <input type="checkbox" checked={form.isDentist || form.role === 'dentist'} disabled={form.role === 'dentist'} onChange={(e) => setForm({ ...form, isDentist: e.target.checked })} />
              <span>{t.settings.dentistSeat}</span>
            </label>
          </>
        )}
      </div>
    </Modal>
  );
}

/** The invitation email again, for a colleague who has not joined yet. */
function ResendInvite({ membershipId }: { membershipId: string }) {
  const { t } = useI18n();
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'off' | 'error'>('idle');
  if (state === 'sent') return <span className="badge ok" style={{ marginInlineEnd: 12 }}>{t.settings.inviteResent}</span>;
  if (state === 'off') return <span className="badge warn" style={{ marginInlineEnd: 12 }}>{t.settings.emailOff}</span>;
  return (
    <button
      type="button"
      className="btn-link"
      style={{ marginInlineEnd: 12 }}
      disabled={state === 'busy'}
      onClick={async () => {
        setState('busy');
        try {
          const r = await api.resendInvite(membershipId);
          setState(r.emailed ? 'sent' : 'off');
        } catch {
          setState('error');
        }
      }}
    >
      {state === 'error' ? t.common.retry : t.settings.resendInvite}
    </button>
  );
}

/** Your own password. Every other session of yours is signed out when it changes. */
function ChangePassword() {
  const { t } = useI18n();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [security, setSecurity] = useState<{ twoStep: boolean; everySignIn: boolean } | null>(null);
  const [forgot, setForgot] = useState<number | null>(null);

  useEffect(() => {
    api.me().then((s) => setSecurity(s.security ?? null)).catch(() => setSecurity(null));
  }, []);

  async function forgetDevices() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.forgetTrustedDevices();
      setForgot(r.forgotten);
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (form.next !== form.confirm) {
      setError(t.auth.passwordsDiffer);
      return;
    }
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await api.changePassword(form.current, form.next);
      setDone(true);
      setForm({ current: '', next: '', confirm: '' });
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head"><h2>{t.settings.account}</h2></div>
      <div className="card-body stack">
        {done ? <div className="notice" role="status"><span>{t.auth.passwordChanged}</span></div> : null}
        {error ? <div className="notice danger" role="alert">{error}</div> : null}
        <div className="grid-2">
          <Field label={t.auth.currentPassword} required>
            <input type="password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} autoComplete="current-password" className="ltr-value" />
          </Field>
          <div />
          <Field label={t.auth.newPassword} hint={t.auth.passwordRule} required>
            <input type="password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} autoComplete="new-password" className="ltr-value" />
          </Field>
          <Field label={t.auth.confirmPassword} required>
            <input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} autoComplete="new-password" className="ltr-value" />
          </Field>
        </div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn" disabled={busy || !form.current || !form.next || !form.confirm} onClick={() => void submit()}>
            {busy ? <Spinner /> : t.auth.changePassword}
          </button>
        </div>
        {security ? (
          <div className="stack" style={{ gap: 8, borderTop: '1px solid var(--line-2)', paddingTop: 16 }}>
            <div className="section-label">{t.settings.twoStep}</div>
            <p style={{ margin: 0, color: 'var(--ink-2)' }}>
              {!security.twoStep ? t.settings.twoStepOff : security.everySignIn ? t.settings.twoStepEvery : t.settings.twoStepNewDevices}
            </p>
            {security.twoStep && !security.everySignIn ? (
              <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void forgetDevices()}>
                  {t.settings.forgetDevices}
                </button>
                {forgot !== null ? <span className="badge ok">{t.settings.devicesForgotten.replace('{n}', String(forgot))}</span> : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Catalogue({ clinicId }: { clinicId: string }) {
  const { t, locale } = useI18n();
  const [items, setItems] = useState<Awaited<ReturnType<typeof api.catalogue>>['items'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CatalogueItem | null>(null);
  const [installing, setInstalling] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setItems(null);
    api.catalogue().then((r) => setItems(r.items)).catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  async function installStandard() {
    setInstalling(true);
    try {
      await api.installStandardCatalogue();
      setNonce((n) => n + 1);
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setInstalling(false);
    }
  }

  if (error) return <ErrorState message={error} />;
  if (!items) return <div className="card"><Skeleton rows={6} /></div>;

  return (
    <>
      {/*
        The code on each row is the clinic's own procedure code. It is what the
        procedure report groups by, so a clinic that wants "which procedures did
        we do most this quarter" to mean something keeps this list tidy.
      */}
      <div className="toolbar">
        <p className="page-sub" style={{ margin: 0 }}>{t.settings.procedures}</p>
        <div className="toolbar-spacer" />
        {/* The standard set, for a clinic that has none or is missing some.
            Adds only what is absent, at zero price. */}
        <button type="button" className="btn btn-ghost" disabled={installing} onClick={() => void installStandard()}>
          {installing ? <Spinner /> : t.settings.installStandard}
        </button>
        <button type="button" className="btn" onClick={() => setCreating(true)}>
          + {t.settings.addProcedure}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="notice warn" style={{ marginBottom: 12 }} role="alert">
          <span>{t.settings.emptyCatalogue}</span>
        </div>
      ) : null}

      <div className="card">
      <div className="card-body flush table-scroll">
        <table>
          <thead>
            <tr>
              <th>{t.inventory.sku}</th>
              <th>{t.inventory.name}</th>
              <th>{t.finance.category}</th>
              <th>{t.appointments.duration}</th>
              <th>{t.finance.amount}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                <td className="ltr-value">{c.code}</td>
                <td>
                  <strong>{locale === 'ar' ? c.nameAr : c.nameEn}</strong>
                  {c.impliesLab ? <span className="badge" style={{ marginInlineStart: 6 }}>lab</span> : null}
                </td>
                <td>
                  <span className="badge">{c.category}</span>
                  {c.specialty && c.specialty !== 'dental' ? (
                    <span className="badge brand" style={{ marginInlineStart: 6 }}>{(t.settings.specialty as Record<string, string>)[c.specialty] ?? c.specialty}</span>
                  ) : null}
                </td>
                <td>{c.durationMinutes} min</td>
                <td className="ltr-value">
                  {new Intl.NumberFormat(locale === 'ar' ? 'ar-SY-u-nu-latn' : 'en-GB', {
                    style: 'currency',
                    currency: c.currency,
                  }).format(c.priceMinor / 100)}
                  {c.priceMinor === 0 ? (
                    <span className="badge warn" style={{ marginInlineStart: 6 }}>{t.settings.noPrice}</span>
                  ) : null}
                </td>
                <td style={{ textAlign: 'end' }}>
                  <button type="button" className="btn-link" onClick={() => setEditing(c)}>
                    {t.remove.edit}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>

      {creating ? (
        <NewProcedure
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {editing ? (
        <EditProcedure
          item={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </>
  );
}

/** The outcomes the database accepts for `resulting_tooth_status`. */
const TOOTH_OUTCOMES = [
  'sound', 'caries', 'restored', 'crowned', 'implant', 'root_canal', 'missing',
  'unerupted', 'retained_primary', 'extracted', 'bridge_pontic', 'veneer', 'fractured', 'watch',
] as const;

/**
 * Edit a procedure. The price is what people come here for; the rest is
 * here because a wrong duration or a wrong chart outcome should not need a
 * new code either. Sends only what changed.
 */
function EditProcedure({ item, onClose, onDone }: { item: CatalogueItem; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const initial = {
    code: item.code,
    nameEn: item.nameEn,
    nameAr: item.nameAr,
    category: item.category,
    price: minorToInput(item.priceMinor, item.currency),
    duration: String(item.durationMinutes),
    requiresTooth: item.requiresTooth,
    impliesLab: item.impliesLab,
    result: item.resultingToothStatus ?? '',
    specialty: item.specialty ?? 'dental',
    requiresZone: item.requiresZone ?? false,
    doseUnit: item.doseUnit ?? '',
    zoneMark: item.zoneMark ?? '',
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patch: Record<string, unknown> = {};
  if (form.code.trim() !== initial.code) patch.code = form.code.trim();
  if (form.nameEn.trim() !== initial.nameEn) patch.nameEn = form.nameEn.trim();
  if (form.nameAr.trim() !== initial.nameAr) patch.nameAr = form.nameAr.trim();
  if (form.category.trim() !== initial.category) patch.category = form.category.trim();
  const priceMinor = parseToMinor(form.price, item.currency);
  if (priceMinor !== null && priceMinor !== item.priceMinor) patch.priceMinor = priceMinor;
  if (Number(form.duration) !== item.durationMinutes && Number(form.duration) > 0) patch.durationMinutes = Number(form.duration);
  if (form.requiresTooth !== initial.requiresTooth) patch.requiresTooth = form.requiresTooth;
  if (form.impliesLab !== initial.impliesLab) patch.impliesLab = form.impliesLab;
  if (form.result !== initial.result) patch.resultingToothStatus = form.result || null;
  if (form.specialty !== initial.specialty) patch.specialty = form.specialty;
  if (form.requiresZone !== initial.requiresZone) patch.requiresZone = form.requiresZone;
  if (form.doseUnit !== initial.doseUnit) patch.doseUnit = form.doseUnit || null;
  if (form.zoneMark !== initial.zoneMark) patch.zoneMark = form.zoneMark || null;

  return (
    <Modal
      title={`${t.remove.edit} — ${item.code}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || Object.keys(patch).length === 0 || !form.nameEn.trim() || !form.code.trim()}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.updateCatalogueItem(item.id, patch);
                onDone();
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : t.settings.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}
        <div className="grid-2">
          <Field label={t.settings.procedureCode} required>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="ltr-value" />
          </Field>
          <Field label={t.finance.category}>
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </Field>
          <Field label={t.inventory.name} required>
            <input value={form.nameEn} onChange={(e) => setForm({ ...form, nameEn: e.target.value })} />
          </Field>
          <Field label={`${t.inventory.name} (العربية)`}>
            <input value={form.nameAr} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} dir="rtl" />
          </Field>
          <Field label={`${t.finance.amount} (${item.currency})`} hint={t.settings.priceHint}>
            <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="ltr-value" inputMode="decimal" />
          </Field>
          <Field label={t.appointments.duration}>
            <input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="ltr-value" inputMode="numeric" />
          </Field>
        </div>
        <Field label={t.settings.catalogueSpecialty}>
          <select value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })}>
            {(['dental', 'dermatology', 'aesthetics'] as const).map((sp) => (
              <option key={sp} value={sp}>{(t.settings.specialty as Record<string, string>)[sp]}</option>
            ))}
            <option value="general">{t.settings.catalogueSpecialtyGeneral}</option>
          </select>
        </Field>
        <label className="row" style={{ gap: 10 }}>
          <input type="checkbox" checked={form.requiresTooth} onChange={(e) => setForm({ ...form, requiresTooth: e.target.checked, requiresZone: e.target.checked ? false : form.requiresZone })} />
          <span>{t.settings.requiresTooth}</span>
        </label>
        <label className="row" style={{ gap: 10 }}>
          <input type="checkbox" checked={form.requiresZone} onChange={(e) => setForm({ ...form, requiresZone: e.target.checked, requiresTooth: e.target.checked ? false : form.requiresTooth })} />
          <span>{t.settings.requiresZone}</span>
        </label>
        <label className="row" style={{ gap: 10 }}>
          <input type="checkbox" checked={form.impliesLab} onChange={(e) => setForm({ ...form, impliesLab: e.target.checked })} />
          <span>{t.settings.impliesLab}</span>
        </label>
        {form.requiresZone ? (
          <div className="grid-2">
            <Field label={t.settings.doseUnit}>
              <select value={form.doseUnit} onChange={(e) => setForm({ ...form, doseUnit: e.target.value })}>
                <option value="">{t.settings.doseUnitNone}</option>
                {Object.keys(t.zones.unit).map((u) => (
                  <option key={u} value={u}>{(t.zones.unit as Record<string, string>)[u]}</option>
                ))}
              </select>
            </Field>
            <Field label={t.settings.zoneMark} hint={t.settings.zoneMarkHint}>
              <select value={form.zoneMark} onChange={(e) => setForm({ ...form, zoneMark: e.target.value })}>
                <option value="">{t.settings.zoneMarkNone}</option>
                {Object.keys(t.zones.mark).map((mk) => (
                  <option key={mk} value={mk}>{(t.zones.mark as Record<string, string>)[mk]}</option>
                ))}
              </select>
            </Field>
          </div>
        ) : null}
        {form.requiresTooth ? (
          <Field label={t.settings.toothOutcome} hint={t.settings.toothOutcomeHint}>
            <select value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })}>
              <option value="">{t.settings.toothOutcomeNone}</option>
              {TOOTH_OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {(t.chart.status as Record<string, string>)[o] ?? o}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * A new procedure code.
 *
 * The code is the reporting key, so it is required and shown first. Changing a
 * price here affects treatments recorded AFTERWARDS only — every treatment
 * already recorded keeps the price it was charged at.
 */
function NewProcedure({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    code: '',
    nameEn: '',
    nameAr: '',
    category: 'general',
    priceMinor: '0',
    durationMinutes: '30',
    requiresTooth: true,
    impliesLab: false,
    specialty: 'dental',
    requiresZone: false,
    doseUnit: '',
    zoneMark: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal
      title={t.settings.addProcedure}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !form.code.trim() || !form.nameEn.trim()}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.createCatalogueItem({
                  code: form.code.trim().toUpperCase(),
                  nameEn: form.nameEn.trim(),
                  nameAr: form.nameAr.trim() || form.nameEn.trim(),
                  category: form.category.trim() || 'general',
                  priceMinor: Math.round(Number(form.priceMinor) * 100) || 0,
                  durationMinutes: Number(form.durationMinutes) || 30,
                  requiresTooth: form.requiresTooth,
                  impliesLab: form.impliesLab,
                  specialty: form.specialty,
                  requiresZone: form.requiresZone,
                  doseUnit: form.doseUnit || null,
                  zoneMark: form.requiresZone ? form.zoneMark || null : null,
                });
                onDone();
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : t.settings.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}
        <div className="grid-2">
          <Field label={t.settings.procedureCode} required>
            <input value={form.code} onChange={set('code')} className="ltr-value" autoFocus />
          </Field>
          <Field label={t.finance.category}>
            <input value={form.category} onChange={set('category')} />
          </Field>
          <Field label={t.inventory.name} required>
            <input value={form.nameEn} onChange={set('nameEn')} />
          </Field>
          <Field label={`${t.inventory.name} (العربية)`}>
            <input value={form.nameAr} onChange={set('nameAr')} dir="rtl" />
          </Field>
          <Field label={t.finance.amount}>
            <input value={form.priceMinor} onChange={set('priceMinor')} inputMode="decimal" className="ltr-value" />
          </Field>
          <Field label={t.appointments.duration}>
            <input
              value={form.durationMinutes}
              onChange={set('durationMinutes')}
              inputMode="numeric"
              className="ltr-value"
            />
          </Field>
        </div>
        <Field label={t.settings.catalogueSpecialty}>
          <select value={form.specialty} onChange={set('specialty')}>
            {(['dental', 'dermatology', 'aesthetics'] as const).map((sp) => (
              <option key={sp} value={sp}>{(t.settings.specialty as Record<string, string>)[sp]}</option>
            ))}
            <option value="general">{t.settings.catalogueSpecialtyGeneral}</option>
          </select>
        </Field>
        <label className="row" style={{ gap: 8 }}>
          <input
            type="checkbox"
            checked={form.requiresTooth}
            onChange={(e) => setForm({ ...form, requiresTooth: e.target.checked, requiresZone: e.target.checked ? false : form.requiresZone })}
          />
          <span>{t.settings.requiresTooth}</span>
        </label>
        <label className="row" style={{ gap: 8 }}>
          <input
            type="checkbox"
            checked={form.requiresZone}
            onChange={(e) => setForm({ ...form, requiresZone: e.target.checked, requiresTooth: e.target.checked ? false : form.requiresTooth })}
          />
          <span>{t.settings.requiresZone}</span>
        </label>
        {form.requiresZone ? (
          <div className="grid-2">
            <Field label={t.settings.doseUnit}>
              <select value={form.doseUnit} onChange={set('doseUnit')}>
                <option value="">{t.settings.doseUnitNone}</option>
                {Object.keys(t.zones.unit).map((u) => (
                  <option key={u} value={u}>{(t.zones.unit as Record<string, string>)[u]}</option>
                ))}
              </select>
            </Field>
            <Field label={t.settings.zoneMark} hint={t.settings.zoneMarkHint}>
              <select value={form.zoneMark} onChange={set('zoneMark')}>
                <option value="">{t.settings.zoneMarkNone}</option>
                {Object.keys(t.zones.mark).map((mk) => (
                  <option key={mk} value={mk}>{(t.zones.mark as Record<string, string>)[mk]}</option>
                ))}
              </select>
            </Field>
          </div>
        ) : null}
        <label className="row" style={{ gap: 8 }}>
          <input
            type="checkbox"
            checked={form.impliesLab}
            onChange={(e) => setForm({ ...form, impliesLab: e.target.checked })}
          />
          <span>{t.lab.title}</span>
        </label>
      </div>
    </Modal>
  );
}

function Audit({ clinicId }: { clinicId: string }) {
  const { t, formatDate, formatTime } = useI18n();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.auditLog>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    api.auditLog().then(setData).catch((e) => setError(describe(e, t)));
  }, [clinicId, t]);

  if (error) return <ErrorState message={error} />;
  if (!data) return <div className="card"><Skeleton rows={8} /></div>;

  return (
    <div className="stack">
      <div className="notice">
        <span>{data.note}</span>
      </div>
      <div className="card">
        {data.events.length === 0 ? (
          <EmptyState title={t.dashboard.noActivity} />
        ) : (
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.appointments.time}</th>
                  <th>{t.inventory.action}</th>
                  <th>{t.finance.description}</th>
                  <th>{t.settings.auditWho}</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((e) => (
                  <tr key={e.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(e.recordedAt)} <span className="ltr-value">{formatTime(e.recordedAt)}</span>
                    </td>
                    <td><code style={{ fontSize: 12 }}>{e.action}</code></td>
                    <td>{e.summary ?? '—'}</td>
                    <td>{e.actor ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * Where patients come from: the clinic's own list, chosen at registration and
 * counted in the reports. A source patients already have is retired, not
 * deleted, so their records keep it; a retired one can be used again.
 */
function Sources({ clinicId }: { clinicId: string }) {
  const { t, tx, locale } = useI18n();
  const [rows, setRows] = useState<PatientSource[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<PatientSource | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    api
      .patientSources(true)
      .then((r) => setRows(r.sources))
      .catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  if (error) return <ErrorState message={error} />;
  if (!rows) return <div className="card"><Skeleton rows={5} /></div>;

  const reload = () => setNonce((n) => n + 1);
  const act = async (fn: () => Promise<unknown>, msg?: string) => {
    setNotice(null);
    try {
      await fn();
      if (msg) setNotice(msg);
      reload();
    } catch (e) {
      setError(describe(e, t));
    }
  };

  return (
    <div className="stack">
      {notice ? <div className="notice" role="status"><span>{notice}</span></div> : null}
      <div className="card">
        <div className="card-head">
          <div>
            <h2>{t.sources.title}</h2>
            <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>{t.sources.sub}</p>
          </div>
          <button type="button" className="btn btn-sm" onClick={() => setAdding(true)}>+ {t.sources.add}</button>
        </div>
        {rows.length === 0 ? (
          <EmptyState title={t.sources.empty} />
        ) : (
          <table className="table">
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={r.active ? '' : 'is-muted'}>
                  <td>
                    <strong>{locale === 'ar' && r.nameAr ? r.nameAr : r.nameEn}</strong>
                    <div className="muted" style={{ fontSize: 12 }}>{locale === 'ar' ? r.nameEn : (r.nameAr ?? '')}</div>
                  </td>
                  <td className="muted">{tx(t.sources.patientsCount, { n: r.patients ?? 0 })}</td>
                  <td>{r.active ? null : <span className="badge">{t.sources.retired}</span>}</td>
                  <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                    <button type="button" className="btn btn-xs btn-ghost" onClick={() => setEditing(r)}>{t.patientEdit.edit}</button>{' '}
                    {r.active ? (
                      <button type="button" className="btn btn-xs btn-ghost" onClick={() => act(() => api.updatePatientSource(r.id, { active: false }))}>{t.sources.retire}</button>
                    ) : (
                      <button type="button" className="btn btn-xs btn-ghost" onClick={() => act(() => api.updatePatientSource(r.id, { active: true }))}>{t.sources.restore}</button>
                    )}{' '}
                    {(r.patients ?? 0) === 0 ? (
                      <button type="button" className="btn btn-xs btn-danger" onClick={() => act(() => api.removePatientSource(r.id))}>{t.sources.remove}</button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {adding || editing ? (
        <SourceModal
          source={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onDone={() => {
            setAdding(false);
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function SourceModal({ source, onClose, onDone }: { source: PatientSource | null; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [nameEn, setNameEn] = useState(source?.nameEn ?? '');
  const [nameAr, setNameAr] = useState(source?.nameAr ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (source) await api.updatePatientSource(source.id, { nameEn, nameAr: nameAr || null });
      else await api.createPatientSource({ nameEn, nameAr: nameAr || null });
      onDone();
    } catch (e) {
      setError(describe(e, t));
      setBusy(false);
    }
  }
  return (
    <Modal
      title={source ? t.patientEdit.edit : t.sources.add}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>{t.sources.cancel}</button>
          <button type="button" className="btn" onClick={save} disabled={busy || !nameEn.trim()}>{busy ? <Spinner /> : t.sources.save}</button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}
        <Field label={t.sources.nameEn} required>
          <input value={nameEn} onChange={(e) => setNameEn(e.target.value)} maxLength={80} autoFocus />
        </Field>
        <Field label={t.sources.nameAr}>
          <input value={nameAr} onChange={(e) => setNameAr(e.target.value)} maxLength={80} dir="rtl" />
        </Field>
      </div>
    </Modal>
  );
}
